import { useEffect, useRef, useState } from 'react'
import { Track } from '../types/audio'
import './MusicPlayer.css'

interface MusicPlayerProps {
  currentTrack: Track | null
  isPlaying: boolean
  onPlayPause: () => void
  onNextTrack: () => void
  onPrevTrack: () => void
}

export function MusicPlayer({ 
  currentTrack, 
  isPlaying, 
  onPlayPause,
  onNextTrack,
  onPrevTrack 
}: MusicPlayerProps) {
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(error => {
          console.error('Error playing audio:', error)
        })
      } else {
        audioRef.current.pause()
      }
    }
  }, [isPlaying, currentTrack])

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume
    }
  }, [volume])

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!currentTrack) return

      switch (e.code) {
        case 'Space':
          e.preventDefault()
          onPlayPause()
          break
        case 'ArrowLeft':
          if (audioRef.current) {
            audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 5)
          }
          break
        case 'ArrowRight':
          if (audioRef.current) {
            audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 5)
          }
          break
        case 'ArrowUp':
          setVolume(prev => Math.min(1, prev + 0.1))
          break
        case 'ArrowDown':
          setVolume(prev => Math.max(0, prev - 0.1))
          break
        case 'KeyM':
          setVolume(prev => prev === 0 ? 1 : 0)
          break
        case 'KeyN':
          onNextTrack()
          break
        case 'KeyP':
          onPrevTrack()
          break
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [currentTrack, duration, onPlayPause, onNextTrack, onPrevTrack])

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime)
    }
  }

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration)
    }
  }

  const handleEnded = () => {
    onNextTrack()
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number(e.target.value)
    setCurrentTime(time)
    if (audioRef.current) {
      audioRef.current.currentTime = time
    }
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  return (
    <div className="music-player">
      <audio
        ref={audioRef}
        src={currentTrack?.url || ''}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
      />
      <div className="track-info">
        {currentTrack ? (
          <>
            {currentTrack.coverArt && (
              <img 
                src={currentTrack.coverArt} 
                alt={`${currentTrack.title} cover`} 
                className="track-cover"
              />
            )}
            <div>
              <h3>{currentTrack.title}</h3>
              <p className="artist-name">{currentTrack.artist}</p>
            </div>
          </>
        ) : (
          <h3>Select a track</h3>
        )}
      </div>
      <div className="controls">
        <button 
          className="prev-button" 
          onClick={onPrevTrack}
          aria-label="Previous track (P)"
          disabled={!currentTrack}
        >
          ⏮
        </button>
        <button 
          className="play-pause-button" 
          onClick={onPlayPause}
          aria-label={isPlaying ? "Pause (Space)" : "Play (Space)"}
          disabled={!currentTrack}
        >
          {isPlaying ? '⏸' : '▶'}
        </button>
        <button 
          className="next-button" 
          onClick={onNextTrack}
          aria-label="Next track (N)"
          disabled={!currentTrack}
        >
          ⏭
        </button>
      </div>
      <div className="progress">
        <span className="time">{formatTime(currentTime)}</span>
        <label htmlFor="track-progress">
          <input
            id="track-progress"
            type="range"
            min="0"
            max={duration}
            value={currentTime}
            onChange={handleSeek}
            className="progress-bar"
            title="Track progress (Left/Right arrows to seek)"
            disabled={!currentTrack}
          />
        </label>
        <span className="time">{formatTime(duration)}</span>
      </div>
      <div className="volume">
        <span role="img" aria-label={volume === 0 ? "Muted (M)" : "Volume (M)"}>
          {volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
        </span>
        <label htmlFor="volume-control">
          <input
            id="volume-control"
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={volume}
            className="volume-slider"
            title="Volume control (Up/Down arrows)"
            onChange={(e) => setVolume(Number(e.target.value))}
          />
        </label>
      </div>
    </div>
  )
} 